// ¿La persona está EN Atlas en este momento, en esta pestaña? Una sola definición para los dos que
// la necesitan: la presencia (decide si se manda el aviso al celular) y la campana (decide si una
// generación que termina suma el puntito rojo). Si cada uno tuviera la suya, podrían contradecirse:
// "no te aviso porque estabas mirando" y a la vez "te dejo el puntito porque no estabas".
//
// Estar en Atlas = la pestaña está VISIBLE y la ventana tiene el FOCO. Sólo con "visible" (lo que
// había antes), quien dejaba Atlas a la vista y se iba a otra app (WhatsApp, el editor) contaba como
// mirando: no le llegaba el aviso de que su imagen estaba lista, que es justo cuando lo necesita.
// Con dos monitores, mirar Atlas sin foco cuenta como "no está": llega un aviso de más, que es mucho
// mejor que uno que falta.

/** La regla, sola, para poder probarla. */
export function calcularMirando(visible: boolean, conFoco: boolean): boolean {
  return visible && conFoco;
}

/**
 * El estado de esta pestaña ahora. Sin `document` (tests, SSR) no hay nadie mirando.
 *
 * `document.hasFocus()` es `true` también cuando el foco está adentro de un iframe de la página (un
 * video, el editor, el sandbox de las animaciones): hacer clic ahí no es irse de Atlas.
 */
export function estaMirandoAhora(): boolean {
  if (typeof document === 'undefined') return false;
  const visible = document.visibilityState === 'visible';
  const conFoco = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  return calcularMirando(visible, conFoco);
}

/**
 * Avisa cada vez que cambia `estaMirandoAhora()`. Devuelve la función para dejar de escuchar.
 *
 * El `blur` de la ventana se re-chequea en la vuelta siguiente: cuando la persona hace clic en un
 * iframe de la página, la ventana dispara `blur` ANTES de que el foco llegue al iframe, y en ese
 * instante `hasFocus()` todavía puede decir que no. Un tick después ya dice la verdad.
 */
export function escucharMirando(alCambiar: (mirando: boolean) => void): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  let ultimo = estaMirandoAhora();
  let pendiente: ReturnType<typeof setTimeout> | null = null;

  const revisar = () => {
    const ahora = estaMirandoAhora();
    if (ahora === ultimo) return;
    ultimo = ahora;
    alCambiar(ahora);
  };
  const revisarDespues = () => {
    if (pendiente) clearTimeout(pendiente);
    pendiente = setTimeout(() => {
      pendiente = null;
      revisar();
    }, 0);
  };

  document.addEventListener('visibilitychange', revisar);
  window.addEventListener('focus', revisar);
  window.addEventListener('blur', revisarDespues);

  return () => {
    if (pendiente) clearTimeout(pendiente);
    document.removeEventListener('visibilitychange', revisar);
    window.removeEventListener('focus', revisar);
    window.removeEventListener('blur', revisarDespues);
  };
}
